import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { changePasswordFirstLogin, logout, selectCurrentUser } from '../../features/auth/authSlice';
import { useToast } from '../../contexts/ToastContext';
import OmTextField from '../ui/OmTextField';
import OmButton from '../ui/OmButton';
import OMLogo from '../../assets/OM-logo.png';

function PasswordChangeForm() {
  const [formData, setFormData] = useState({
    tempPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const toast = useToast();
  const currentUser = useSelector(selectCurrentUser);
  
  // Get user data from location state first, then fallback to Redux user state
  const { userId, username } = location.state || {};
  const finalUserId = userId || currentUser?.userId;
  const finalUsername = username || currentUser?.username;
  
  // Redirect if no user data available from either source - use useEffect to avoid render-time navigation
  useEffect(() => {
    if (!finalUserId || !finalUsername) {
      navigate('/login', { replace: true });
    }
  }, [finalUserId, finalUsername, navigate]);

  // Don't render form if user data is not available
  if (!finalUserId || !finalUsername) {
    return null;
  }

  const validateForm = () => {
    const newErrors = {};

    // Temporary password validation
    if (!formData.tempPassword.trim()) {
      newErrors.tempPassword = 'Current password is required';
    }

    // New password validation
    if (!formData.newPassword.trim()) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters long';
    } else {
      // Password strength validation
      const hasUpperCase = /[A-Z]/.test(formData.newPassword);
      const hasLowerCase = /[a-z]/.test(formData.newPassword);
      const hasNumbers = /\d/.test(formData.newPassword);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword);
      
      if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
        newErrors.newPassword = 'Password must contain uppercase, lowercase, numbers, and special characters';
      }
    }

    // Confirm password validation
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      const resultAction = await dispatch(changePasswordFirstLogin({
        userId: finalUserId,
        tempPassword: formData.tempPassword,
        newPassword: formData.newPassword
      }));

      if (changePasswordFirstLogin.fulfilled.match(resultAction)) {
        // Clear auth state to prevent automatic login and force manual re-authentication
        dispatch(logout());
        
        toast.showSuccess('Password changed successfully. Please log in with your new password.');
        // After successful password change, redirect to login for re-authentication
        navigate('/login', { 
          replace: true,
          state: { 
            username: finalUsername // Pre-populate username 
          }
        });
      } else {
        throw new Error(resultAction.payload || 'Password change failed');
      }
    } catch (error) {
      toast.showError(error.message || 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-silver-mist px-4">
      <div className="max-w-md w-full">
        <div className="bg-white shadow-lg rounded-lg px-8 py-8">
          <div className="text-center mb-8">
            <img 
              src={OMLogo} 
              alt="Old Mutual Logo" 
              className="mx-auto h-16 w-auto mb-6"
            />
            <h2 className="text-3xl font-display text-charcoal-gray mb-3">
              Change Your Password
            </h2>
            <p className="text-slate-gray text-sm">
              Welcome <span className="font-semibold">{finalUsername}</span>! 
              Please change your temporary password to continue.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <OmTextField
                id="tempPassword"
                name="tempPassword"
                type="password"
                label="Current Password"
                placeholder="Enter your temporary password"
                value={formData.tempPassword}
                onChange={(e) => handleInputChange('tempPassword', e)}
                errorMessage={errors.tempPassword}
                alwaysShowError={!!errors.tempPassword}
                required
                fullWidth
              />
            </div>

            <div>
              <OmTextField
                id="newPassword"
                name="newPassword"
                type="password"
                label="New Password"
                placeholder="Enter your new password"
                value={formData.newPassword}
                onChange={(e) => handleInputChange('newPassword', e)}
                errorMessage={errors.newPassword}
                alwaysShowError={!!errors.newPassword}
                required
                fullWidth
              />
            </div>

            <div>
              <OmTextField
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                label="Confirm New Password"
                placeholder="Confirm your new password"
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e)}
                errorMessage={errors.confirmPassword}
                alwaysShowError={!!errors.confirmPassword}
                required
                fullWidth
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <OmButton
              type="submit"
              variant="primary"
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading ? 'Changing Password...' : 'Change Password'}
            </OmButton>
            
            <OmButton
              type="button"
              buttonType="outlined-button"
              buttonSize="large"
              leadingIcon="arrow_back"
              onClick={() => navigate('/login')}
              className="flex-1 sm:flex-none sm:min-w-[140px]"
            >
              Back to Login
            </OmButton>
          </div>
          </form>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2">Password Requirements:</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• At least 8 characters long</li>
              <li>• Contains uppercase letters (A-Z)</li>
              <li>• Contains lowercase letters (a-z)</li>
              <li>• Contains numbers (0-9)</li>
              <li>• Contains special characters (!@#$%^&*)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PasswordChangeForm;